/**
 * hullpanel.jsx — the ship a chapter teaches on, beside the ship that makes the point visible.
 *
 * FOUNDER, 2026-08-17: "chapter picks the hull that shows the point"
 *
 * ── WHY THIS PANEL EXISTS AT ALL ──────────────────────────────────────────────────────────────
 * The fleet powered Ship mode and 45 server engines, and the 2M ladder named no ship in any of its
 * twenty-one chapters. Chapters 6 and 7 — the stability triangle and the ship as a structure —
 * returned an EMPTY reading list and no hull: the two chapters that exist to teach hull form had
 * nothing of ours in them, while eight fully-declared hulls sat one tab away.
 *
 * ── IT RENDERS, IT DOES NOT COMPUTE ───────────────────────────────────────────────────────────
 * Every number arrives from /api/chapter-hull already resolved, because the app aliases fleet.mjs to
 * a shim that throws. Nothing here does arithmetic; if a figure looks wrong the engine is wrong, and
 * there is exactly one place to go and look.
 *
 * ── THE TWO LAYERS ARE RENDERED APART, NOT MERELY STORED APART ────────────────────────────────
 * Values carry a basis — compute, quote, or null — and each one is shown with its badge. The
 * pedagogic prose ("this hull shows the point because…") is mine, and sits under its own heading
 * with its own caption saying so. A candidate must never have to guess which sentences he can
 * check. FP-018: prose may dress a fact but never father one.
 */
import { useWidgetData } from '../../../packages/ankr-glass/src/react.jsx';
import { plan } from '../../../packages/ankr-vahana/src/plan.mjs';
import { API } from './surfaces.jsx';
import { authHeaders } from './identity.mjs';

const W = { hull: { slug: 'chapter-hull', layout: 'auto-table',
                    wire: { kind: 'rest', route: '/api/chapter-hull', method: 'GET', field: 'hull' } } };

const pretty = (s) => String(s || '')
  .replace(/_m$/, ' (m)').replace(/_t$/, ' (t)').replace(/_mm$/, ' (mm)')
  .replace(/_t_per_cm$/, ' (t/cm)').replace(/_m2$/, ' (m²)').replace(/_/g, ' ');

const show = (v) => {
  if (v === true) return 'yes';
  if (v === false) return 'no';
  if (Array.isArray(v)) return `${v.length} · ${v.join(', ')}`;
  return String(v);
};

/** compute · quote · null, said in one word beside the number it describes. */
function Basis({ of }) {
  const b = of?.basis;
  const title = b === 'compute' ? 'calculated by an engine from this hull’s own declaration'
    : b === 'quote' ? 'lifted verbatim from this hull’s declaration'
    /* The fourth badge, and the one a candidate most needs. A worked example's premises were chosen
       by whoever set the question; they are not measurements of this ship and must not read as any. */
    : b === 'given' ? 'an input chosen for this worked example — not a fact about this ship'
    : 'this hull does not declare it — stated, not filled in';
  return <span className={`hull-basis hull-basis-${b}`} title={title}>{b}</span>;
}

/**
 * THE QUESTION, BEFORE THE ANSWER.
 *
 * A lift is worked from figures somebody supplied, and the panel shows them first with their own
 * badges — the chosen ones as `given`, the ship's own as `quote` or `compute`. A candidate who
 * cannot see which is which is being invited to treat a teacher's assumption as a property of the
 * ship, which is how a worked example turns into a false belief about a real hull.
 */
function Givens({ of, note }) {
  if (!of) return null;
  return (
    <div className="hull-givens">
      <div className="g-label">Worked from</div>
      <ul className="hull-given-list">
        {Object.entries(of).map(([k, v]) => (
          <li key={k} className="g-body">
            <span className="g-dim">{pretty(k)}</span>{' '}
            {v.basis === 'null' ? <span className="g-dim">not declared</span> : show(v.value)}
            <Basis of={v} />
          </li>
        ))}
      </ul>
      {note && <p className="g-body g-dim hull-caption">{note}</p>}
    </div>
  );
}

/** One value cell. A silence renders as a silence and keeps its reason in the title. */
function Cell({ of }) {
  if (!of) return <td className="hull-cell g-dim">—</td>;
  if (of.basis === 'null')
    return <td className="hull-cell g-dim" title={of.reason}>not declared <Basis of={of} /></td>;
  return <td className="hull-cell">{show(of.value)} <Basis of={of} /></td>;
}

/**
 * Flatten whichever shape the engine returned into (label, primaryValue, contrastValue) rows.
 *
 * Three shapes exist because three kinds of question are being asked: hydro and condition return a
 * flat set of quantities, a declaration quote returns them grouped by the block they were lifted
 * from, and the scantling table returns one set per structural member. They are normalised here
 * rather than in the payload so the wire keeps saying what the engine actually produced.
 */
function rowsOf(primary, contrast) {
  const out = [];
  const pair = (label, a, b) => out.push({ label, a, b });

  if (primary?.members) {
    for (const m of primary.members) {
      const other = (contrast?.members || []).find((x) => x.key === m.key);
      for (const [k, v] of Object.entries(m))
        if (v && typeof v === 'object' && v.basis) pair(`${m.label} · ${pretty(k)}`, v, other?.[k]);
    }
    return out;
  }
  const facts = primary?.facts || {};
  const nested = Object.values(facts).some((v) => v && !v.basis);
  if (nested) {
    for (const [block, inner] of Object.entries(facts))
      for (const [k, v] of Object.entries(inner || {}))
        pair(`${block.toLowerCase()} · ${pretty(k)}`, v, contrast?.facts?.[block]?.[k]);
    return out;
  }
  for (const [k, v] of Object.entries(facts)) pair(pretty(k), v, contrast?.facts?.[k]);
  return out;
}

function Principal({ of }) {
  if (!of || of.absent) return null;
  return (
    <div className="hull-card">
      <div className="g-label">{of.ship}</div>
      <div className="g-body hull-name">{of.name}</div>
      <p className="g-body g-dim hull-type">{of.type}</p>
      <p className="g-body g-dim hull-dims">
        LBP {of.LBP} m · B {of.B} m · D {of.D} m · summer draught {of.summer_draft} m
      </p>
    </div>
  );
}

export default function HullPanel({ level, chapter }) {
  /* WHO IS ASKING TRAVELS WITH THE ASK. The reading and method calls went out anonymous for weeks
     and refused every gated chapter to a learner who was signed in — "it logs and throws out". This
     endpoint is gated by the same call they are, so it would have inherited the same fault; the
     headers go on at birth rather than after a founder finds it on his own paid ladder. */
  const auth = authHeaders();
  const q = useWidgetData(W.hull, chapter ? { chapter, level } : {},
                          { baseUrl: API, plan, enabled: Boolean(chapter), headers: auth });
  const d = q.data || {};

  /* NOTHING, LOUDLY, IS STILL NOTHING ON SCREEN. Sixteen of twenty-one 2M chapters declare no hull
     because a hull would be scenery there. That is an editorial position and the wire carries it,
     but the learner should simply not see a panel — an empty box captioned "no ship here" is noise
     on every navigation chapter in the ladder. */
  if (q.loading) return null;
  if (!d || d.kind === 'null' || !d.hull) return null;
  /* A gated chapter is handled by the chapter's own invitation, which has already rendered above.
     Repeating it here would promise a learner the same thing twice in one scroll. */
  if (d.kind === 'gated') return null;

  const rows = rowsOf(d.hull, d.contrast?.absent ? null : d.contrast);

  return (
    <div className="g-glass shell-panel">
      <div className="g-label">
        Taught on {d.hull.name}{d.contrast?.absent ? '' : ` · against ${d.contrast?.name}`}
      </div>

      <div className="hull-cards">
        <Principal of={d.hull} />
        {d.contrast?.absent
          ? (
            /* THE MISSING SECOND SHIP IS THE LESSON, NOT A GAP. Chapter 12 has no contrast because
               only one hull in the fleet declares the weight list a loaded KG needs, and every other
               one answers "it is not a number to assume". Printing that refusal is worth more than
               any second ship invented to fill the space. */
            <div className="hull-card hull-card-absent">
              <div className="g-label">no second ship</div>
              <p className="g-body g-dim">{d.contrast.reason}</p>
            </div>
          )
          : <Principal of={d.contrast} />}
      </div>

      <Givens of={d.hull?.givens} note={d.hull?.givens_are} />

      {rows.length > 0 && (
        <div className="hull-scroll">
          <table className="hull-table">
            <thead>
              <tr>
                <th />
                <th>{d.hull.ship}</th>
                {!d.contrast?.absent && <th>{d.contrast?.ship}</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.label}>
                  <td className="hull-key g-dim">{r.label}</td>
                  <Cell of={r.a} />
                  {!d.contrast?.absent && <Cell of={r.b} />}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── THE JUDGEMENT, FENCED ────────────────────────────────────────────────────────────
          Everything above is compute or quote and can be checked against the engine. Everything
          below is a teaching claim about why these hulls were chosen, and the caption says whose
          claim it is. The status line is not decoration: the founder ruled that a chapter picks the
          hull that shows the point, and he has not yet ruled on WHICH hull for any of these five. */}
      <div className="hull-judgement">
        <div className="g-label">Why this ship</div>
        <p className="g-body">{d.judgement?.shows}</p>
        <p className="g-body g-dim">{d.judgement?.why_this_hull}</p>
        <p className="g-body g-dim hull-caption">
          {d.status_note} Teaching judgement — not a computed or quoted fact; every figure above
          carries its own basis.
        </p>
      </div>
    </div>
  );
}
